import { Code, Group, Stack, Text } from "@mantine/core";
import type { UploadRowError } from "../../api/types";

/**
 * Inline row-level parse errors from a POST ingest/replace response. This is the
 * only place the BE returns parse errors — GET /uploads/{id} (the detail page)
 * does not — so both the upload portal and the replace prompt render them here
 * at upload time rather than deep-linking. Shared so the two callers can't drift.
 *
 * `max` caps how many rows render before collapsing the rest into a "+ N more"
 * line, so a file with hundreds of bad rows doesn't blow out the layout.
 */
export function UploadRowErrorList({
  errors,
  max = 50,
}: {
  errors: UploadRowError[];
  max?: number;
}) {
  const shown = errors.slice(0, max);
  return (
    <Stack gap={4}>
      {shown.map((e, i) => (
        <Group key={`${e.code}-${e.row_number}-${e.column ?? "-"}-${i}`} gap="xs" wrap="nowrap">
          <Text size="xs" ff="monospace" c="dimmed" w={40} ta="right">
            r{e.row_number}
          </Text>
          <Code fz="xs">{e.code}</Code>
          <Text size="xs">
            {e.message}
            {e.column ? ` (${e.column})` : ""}
          </Text>
        </Group>
      ))}
      {errors.length > shown.length ? (
        <Text size="xs" c="dimmed" pl={44}>
          + {errors.length - shown.length} more
        </Text>
      ) : null}
    </Stack>
  );
}
